import React from 'react';
import { RefreshCw, FileCheck, AlertTriangle, CreditCard } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { FeatureCard } from '../components/ui/FeatureCard';

export const PayPalSyncAiPage: React.FC = () => {
    usePageMetadata({
        title: "PayPal Sync AI | Automatyczna Synchronizacja Księgowości",
        description: "Koniec z ręcznym rozliczaniem płatności PayPal. Nasz system AI automatycznie synchronizuje transakcje z Twoją księgowością, oszczędzając godziny pracy.",
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "PayPal Sync AI",
            "serviceType": "Automatyzacja Księgowości",
            "provider": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl"
            },
            "description": "Narzędzie AI do automatycznej synchronizacji transakcji PayPal z systemem księgowym. Eliminuje ręczne uzgadnianie płatności i wykrywa anomalie.",
            "url": "https://biznesbezklikania.pl/rozwiazania/paypal-sync-ai"
        }
    });

    const features = [
        { icon: <RefreshCw className="w-8 h-8" />, title: 'Synchronizacja w czasie rzeczywistym', description: 'System co godzinę pobiera nowe transakcje z PayPal i importuje je do Twojego programu księgowego.' },
        { icon: <FileCheck className="w-8 h-8" />, title: 'Automatyczne dopasowanie', description: 'Inteligentny algorytm dopasowuje płatności do faktur po numerze zamówienia, emailu lub kwocie.' },
        { icon: <AlertTriangle className="w-8 h-8" />, title: 'Wykrywanie anomalii', description: 'Otrzymasz alert o podwójnych płatnościach, niedopłatach lub transakcjach, które nie pasują do żadnej faktury.' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="blue" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                            <CreditCard className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-blue-500 via-primary-light to-blue-500 dark:from-blue-400 dark:via-primary-dark dark:to-blue-400 bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            PayPal Sync AI
                        </h1>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-10">
                            Koniec z ręcznym rozliczaniem płatności PayPal! Nasz silnik automatycznie synchronizuje transakcje z Twoim systemem księgowym, oszczędzając godziny pracy.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <LeadFormModal
                            productSlug="paypal-sync-ai"
                            triggerLabel="Zautomatyzuj księgowość"
                            triggerClassName="px-8 py-4 font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl hover:shadow-xl hover:shadow-blue-500/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group"
                        />
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Features Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="lines" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Poświęć czas na biznes, nie na papierologię
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Nasz system to Twój wirtualny księgowy, który nigdy się nie myli.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {features.map((feature, index) => (
                            <RevealOnScroll key={feature.title} delay={index * 100}>
                                <FeatureCard
                                    icon={feature.icon}
                                    title={feature.title}
                                    description={feature.description}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/paypal-sync-ai" />
        </>
    );
};
